<script setup lang="ts">
import type { ResolvedMonospacePrimitiveField } from '#layers/data-model/types/schema/resolved-schema';
import { defineShortcuts } from '@nuxt/ui/composables';
import { reactive, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { isNil } from '#layers/base';
import { parseRawValue, serializeRawValue, validateRawValue } from '~/utils/raw-value';

const props = defineProps<{
	value: unknown;
	primitiveField?: ResolvedMonospacePrimitiveField;
	disabled?: boolean;
}>();

const emit = defineEmits<{
	close: [result: { value: unknown } | false];
}>();

const formId = useId();
const { t } = useI18n();

const state = reactive({
	value: props.primitiveField?.type ? serializeRawValue(props.value, props.primitiveField.type) : String(props.value),
});

defineShortcuts({
	meta_enter: {
		usingInput: true,
		handler: saveEdit,
	},
});

function validateEditForm() {
	const error = props.primitiveField?.type ? validateRawValue(state.value, props.primitiveField?.type) : false;

	if (error) {
		return [{
			name: 'value',
			message: t(error),
		}];
	}

	return [];
}

function saveEdit() {
	try {
		emit('close', { value: props.primitiveField ? parseRawValue(state.value, props.primitiveField.type) : state.value });
	}
	catch (error) {
		console.error(error);
		// Validation should have caught this, but just in case
	}
}
</script>

<template>
	<DConfirmDialog
		:title="$t('common.edit_raw_value')"
		:confirm="{
			form: formId,
			label: $t('common.save'),
			type: 'submit',
			onClick: saveEdit,
			disabled,
		}"
		@close="emit('close', $event as false)"
	>
		<template #body>
			<DForm
				:id="formId"
				:state
				:validate="validateEditForm"
				:validate-on="['input']"
				@submit="saveEdit"
			>
				<DFormField name="value" :label="$t('common.raw_value')" hide-label>
					<DTextarea
						v-model="state.value"
						:placeholder="isNil(value) ? 'NULL' : ''"
						:rows="3"
						:disabled
						autofocus
						class="font-mono"
					/>
				</DFormField>
			</DForm>
		</template>
	</DConfirmDialog>
</template>
